import { useState, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { GlobeIcon } from './icons/GlobeIcon'
import { BellIcon } from './icons/BellIcon'
import { LogoutIcon } from './icons/LogoutIcon'
import { useAuth } from '../auth/useAuth'
import { DirtyContext } from '../hooks/useDirty'
import { useNotifications } from '../context/NotificationsContext'
import { UnsavedChangesDialog } from './UnsavedChangesDialog'
import { NotificationDropdown } from './NotificationDropdown'
import { ThemeToggleButton } from './ThemeToggleButton'
import { LanguageSelector } from './LanguageSelector'
import { hasDirtyOpenEntityEditor, markAllEntityEditorsClean } from '../lib/entityEditorRegistry'
import { useBeforeUnloadGuard } from '../hooks/useUnsavedChangesGuard'
import { Avatar } from './Avatar'

export function TopBar() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { isAuthenticated, setAuth, userName, userEmail, tenantUrl, studioRole, entitlementsAvailable, entitlementsPending } = useAuth()
  // MS-866 AC6 — hide the bell while entitlements are known-unavailable (Decision
  // 3: not while merely pending, so a cached session doesn't flash the degraded state).
  const entitlementsDegraded = entitlementsAvailable === false && !entitlementsPending
  const { hasActive } = useNotifications()
  const anyDirty = useContext(DirtyContext)?.anyDirty ?? false
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const [showBell, setShowBell] = useState(false)

  // Single app-wide beforeunload guard — replaces the 3 duplicated per-page
  // listeners previously in AgentDetail/GlobalSettings/MambuMcpDetail.
  useBeforeUnloadGuard(anyDirty)

  if (!isAuthenticated) return null

  async function doLogout() {
    try {
      await fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'include' })
    } finally {
      setAuth(false)
    }
  }

  function handleLogoutClick() {
    if (anyDirty || hasDirtyOpenEntityEditor()) {
      setShowLogoutConfirm(true)
    } else {
      doLogout()
    }
  }

  const displayName = userName ?? userEmail ?? 'User'

  return (
    <header className="top-bar">
      <div className="top-bar-left">
        <div
          className="app-brand"
          onClick={() => navigate('/')}
          role="button"
          tabIndex={0}
          onKeyDown={e => { if (e.key === 'Enter') navigate('/') }}
          aria-label={t('appShell.actions.goToHome')}
          style={{ cursor: 'pointer' }}
        >
          <div className="brand-logo">
            <img src="/mambu-logo.png" alt={t('appShell.brandSub')} />
          </div>
          <div className="brand-title">{t('appShell.brand')}</div>
        </div>
        {tenantUrl && (
          <div className="top-bar-tenant" title={tenantUrl}>
            <span className="top-bar-tenant-icon" aria-hidden="true"><GlobeIcon size={15} /></span>
            <span className="top-bar-tenant-url">{tenantUrl.replace(/^https?:\/\//, '')}</span>
          </div>
        )}
      </div>

      <div className="top-bar-right">
        <ThemeToggleButton />

        <LanguageSelector />

        {studioRole === 'Admin' && !entitlementsDegraded && (
          <div className="top-bar-bell">
            <button
              type="button"
              className="top-bar-icon-btn"
              aria-label={t('topBar.notifications')}
              title={t('topBar.notifications')}
              aria-haspopup="dialog"
              aria-expanded={showBell}
              onClick={() => setShowBell(s => !s)}
            >
              <BellIcon />
              {hasActive && <span className="top-bar-bell-dot" aria-hidden="true" />}
            </button>
            {showBell && <NotificationDropdown onClose={() => setShowBell(false)} />}
          </div>
        )}

        <button
          type="button"
          className="top-bar-icon-btn"
          aria-label={t('topBar.logOut')}
          title={t('topBar.logOut')}
          onClick={handleLogoutClick}
        >
          <LogoutIcon />
        </button>

        <div className="top-bar-user">
          <Avatar className="top-bar-user-avatar" size={16} />
          <span className="top-bar-user-name">{displayName}</span>
        </div>
      </div>

      {showLogoutConfirm && (
        <UnsavedChangesDialog
          onConfirm={() => {
            setShowLogoutConfirm(false)
            markAllEntityEditorsClean()
            doLogout()
          }}
          onCancel={() => setShowLogoutConfirm(false)}
        />
      )}
    </header>
  )
}
